// src/components/TopUpAirtime.js
import OrderOutcome from "./OrderOutcome";
import VerifyBanner from "./VerifyBanner";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navigation from "./Navigation";
import useOnlineStatus from "../pwa/useOnlineStatus";
import RecipientSelector from "./RecipientSelector";
import {
  createOrderQuote,
  createParentOrder,
  getProfile,
  getWalletBalance,
  getRecipients,
  fundParentOrder,
  fulfilParentOrder,
  getParentOrder,
} from "../services/api";

/**
 * Mobile Top-Up — the airtime-only parent-order journey.
 *
 * ADR-001: customer journeys are product-separated. This flow handles AIRTIME
 * items only, for up to three recipients, on the same Parent Order engine.
 *
 * Hand-off: when launched from the Send Money confirmation screen, router state
 * may carry recipient IDs to pre-fill. Router state is a CONVENIENCE HINT, NOT
 * AUTHORIZATION — recipients are always reloaded from the backend, which scopes
 * them to the authenticated customer. If the state is missing or invalid the
 * normal picker opens; the page never fails or redirects because of it.
 *
 * The top-up always creates its OWN quote and Parent Order. It is never appended
 * to an accepted money order.
 */

const MAX_RECIPIENTS = 3;
const QUICK_AMOUNTS = [50, 100, 250, 500];

const LIMITS = {
  AIRTIME_KES_MIN: 10,
  AIRTIME_KES_MAX: 5000,
};

const FALLBACK_RATE = 92.25; // KES per CAD — estimate only; the quote is authoritative

export default function TopUpAirtime() {
  const online = useOnlineStatus(); // money actions need a live connection
  const navigate = useNavigate();
  const { state } = useLocation();

  const [step, setStep] = useState(1);

  // funding context
  const [snplAvailable, setSnplAvailable] = useState(null);
  const [snplStatus, setSnplStatus] = useState(null);
  const [walletCad, setWalletCad] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [verification, setVerification] = useState(null);

  // hand-off
  const [handoff, setHandoff] = useState(null); // { sourceOrderReference }
  const [prefilling, setPrefilling] = useState(false);

  const [recipientCount, setRecipientCount] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [slots, setSlots] = useState([]);
  const [amounts, setAmounts] = useState({});

  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    loadContext();
    applyHandoff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadContext() {
    setContextLoading(true);
    try {
      const profile = await getProfile();
      const u = (profile.data && profile.data.user) || profile.user || profile || {};
      setVerification({ idVerified: Boolean(u.idVerified), phoneVerified: Boolean(u.phoneVerified) });
      const limit = Number(u.snplLimit || 0);
      setSnplAvailable(u.snplAvailable != null ? Number(u.snplAvailable) : limit);
      setSnplStatus((u.snplStatus || (limit > 0 ? "APPROVED" : "UNASSESSED")).toUpperCase());
    } catch (e) {
      setSnplAvailable(0);
    }
    try {
      const wallet = await getWalletBalance();
      // TD-15: spendable = total minus money reserved for orders in progress.
      const spendable = wallet.available && wallet.available.CAD != null ? wallet.available.CAD
        : (wallet.balances && wallet.balances.CAD);
      setWalletCad(Number(spendable || 0));
    } catch (e) {
      setWalletCad(0);
    }
    setContextLoading(false);
  }

  /**
   * Pre-fill from a Send Money hand-off. IDs only — recipient details are
   * reloaded from the backend so nothing stale is trusted, and ownership is
   * enforced server-side. Any failure falls back to the normal picker.
   */
  async function applyHandoff() {
    const ids = Array.isArray(state && state.recipientIds)
      ? state.recipientIds.slice(0, MAX_RECIPIENTS)
      : [];
    if (!ids.length) return;

    setPrefilling(true);
    try {
      const response = await getRecipients();
      const all = (response && (response.recipients || response.data)) || [];
      // Only recipients the backend actually returned for THIS customer.
      const matched = ids
        .map((id) => all.find((r) => r.id === id))
        .filter(Boolean)
        .slice(0, MAX_RECIPIENTS);

      if (matched.length) {
        setRecipientCount(matched.length);
        setSlots(matched);
        setPaymentMethod(null);
        setHandoff({ sourceOrderReference: (state && state.sourceOrderReference) || null });
        setStep(2); // recipients known; go straight to payment
      }
    } catch (e) {
      // Silent fallback — the normal picker is shown.
    } finally {
      setPrefilling(false);
    }
  }

  // ---------- funding ----------

  const availableCap =
    paymentMethod === "SNPL"
      ? Number(snplAvailable || 0)
      : paymentMethod === "WALLET"
      ? Number(walletCad || 0)
      : 0;

  const MIN_SNPL_CAD = 5;
  const snplBlocked = ["FROZEN", "PENDING_REVIEW", "DECLINED"].includes(snplStatus);
  const snplUsable = !snplBlocked && Number(snplAvailable || 0) >= MIN_SNPL_CAD;

  function snplReason() {
    if (snplStatus === "FROZEN") return "Your SNPL credit is temporarily frozen";
    if (snplStatus === "PENDING_REVIEW") return "Your assessment is under review";
    if (snplStatus === "DECLINED") return "Not available on your account right now";
    const amt = Number(snplAvailable || 0);
    if (amt <= 0) return "No credit available — unlock your limit first";
    return `Only CAD ${amt.toFixed(2)} available — minimum CAD ${MIN_SNPL_CAD.toFixed(2)}`;
  }

  function estimatedTotalCad() {
    let total = 0;
    slots.forEach((_, i) => {
      const kes = Number(amounts[i] || 0);
      if (kes > 0) total += kes / FALLBACK_RATE;
    });
    return Math.round(total * 100) / 100;
  }

  const runningTotal = estimatedTotalCad();
  const remaining = Math.round((availableCap - runningTotal) * 100) / 100;
  const overCap = runningTotal > availableCap;

  // ---------- steps ----------

  function chooseCount(n) {
    setRecipientCount(n);
    setSlots(Array(n).fill(null));
    setAmounts({});
    setError(null);
  }

  const chosen = slots.filter(Boolean);
  const chosenIds = chosen.map((r) => r.id);

  function selectRecipient(index, recipient) {
    if (recipient && chosenIds.includes(recipient.id)) {
      setError(`${recipient.recipientName} is already on this order.`);
      return;
    }
    setError(null);
    const next = [...slots];
    next[index] = recipient;
    setSlots(next);
  }

  function setAmount(i, raw) {
    const value = raw.replace(/[^0-9]/g, "");
    setAmounts({ ...amounts, [i]: value });
    setFieldErrors({ ...fieldErrors, [i]: null });
  }

  function validate() {
    const errs = {};
    slots.forEach((slot, i) => {
      if (!slot) { errs[i] = "Choose a recipient"; return; }
      const kes = Number(amounts[i] || 0);
      if (kes === 0) { errs[i] = "Enter a top-up amount"; return; }
      if (kes < LIMITS.AIRTIME_KES_MIN) errs[i] = `Minimum KES ${LIMITS.AIRTIME_KES_MIN}`;
      if (kes > LIMITS.AIRTIME_KES_MAX)
        errs[i] = `Maximum KES ${LIMITS.AIRTIME_KES_MAX.toLocaleString()}`;
    });
    setFieldErrors(errs);
    return Object.values(errs).filter(Boolean).length === 0;
  }

  // ---------- quote / confirm ----------

  async function handleGetQuote() {
    setError(null);
    if (!validate()) return;
    if (overCap) {
      setError(
        `This top-up is CAD ${runningTotal.toFixed(2)} but only CAD ${availableCap.toFixed(
          2
        )} is available.`
      );
      return;
    }
    setLoading(true);
    try {
      const payload = {
        sourceCurrency: "CAD",
        paymentMethod,
        recipients: chosen.map((r, i) => ({
          recipientId: r.id,
          destinationCountry: r.recipientCountry || "KE",
          destinationCurrency: "KES",
          products: [{ productType: "AIRTIME", destinationAmount: Number(amounts[i]) }],
        })),
      };
      const response = await createOrderQuote(payload);
      setQuote(response.quote);
      setStep(4);
    } catch (err) {
      setError(err.message || "Could not price this top-up");
    } finally {
      setLoading(false);
    }
  }

  function genKey() {
    return "topup-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  async function handleConfirm() {
    setError(null);
    setLoading(true);
    // TD-15: create -> fund (reserve wallet money) -> send; a retry reuses the
    // same order so the customer is never charged twice.
    let created = order;
    try {
      if (!created) {
        created = await createParentOrder(
          {
            quoteReference: quote.reference,
            recipients: chosen.map((r) => ({
              recipientId: r.id,
              destinationCountry: r.recipientCountry || "KE",
              notificationPhone: r.recipientPhone || null,
            })),
          },
          genKey()
        );
        setOrder(created);
      }
      const ref = created.order.reference;
      await fundParentOrder(ref);
      await fulfilParentOrder(ref);
      setOrder(await getParentOrder(ref));
      setStep(5);
    } catch (err) {
      if (err.code === "VERIFICATION_REQUIRED" && err.data) {
        setVerification({ idVerified: !err.data.needsIdVerification, phoneVerified: !err.data.needsPhoneVerification });
      }
      const msg = err.message || "Could not complete this top-up";
      setError(created
        ? `${msg} Your top-up ${created.order.reference} has been saved; you can try again without being charged twice.`
        : msg);
    } finally {
      setLoading(false);
    }
  }

  // ---------- render ----------

  return (
    <div>
      <Navigation />
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-1">Mobile Top-Up for Voice &amp; Data</h1>
        <p className="text-gray-600 mb-2">
          Top up phones in Kenya for up to three people — instantly, across
          Safaricom, Airtel and Telkom.
        </p>
        <VerifyBanner verification={verification} />
        <p className="text-sm text-gray-500 mb-4">
          You send mobile credit. They choose how to use it — calls, SMS, or
          buying a data bundle on their network.
        </p>

        {handoff && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded mb-4 text-sm">
            Continuing from your money order
            {handoff.sourceOrderReference ? ` ${handoff.sourceOrderReference}` : ""}. This
            top-up is a <strong>separate order</strong>, priced on its own.
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        {prefilling && <p className="text-gray-500 mb-4">Loading your recipients…</p>}

        {/* STEP 1 — how many recipients */}
        {step === 1 && (
          <div className="border p-4 rounded shadow">
            <h2 className="font-semibold text-lg mb-3">How many phones are you topping up?</h2>
            <div className="flex gap-3 mb-4">
              {[1, 2, 3].map((n) => (
                <label
                  key={n}
                  className={
                    "flex-1 border rounded p-4 text-center cursor-pointer " +
                    (recipientCount === n
                      ? "border-blue-600 bg-blue-50 text-blue-800 font-semibold"
                      : "border-gray-300")
                  }
                >
                  <input
                    type="radio"
                    name="count"
                    className="mr-2"
                    checked={recipientCount === n}
                    onChange={() => chooseCount(n)}
                  />
                  {n} {n === 1 ? "recipient" : "recipients"}
                </label>
              ))}
            </div>
            <button
              className="bg-blue-600 text-white p-2 w-full rounded disabled:opacity-50"
              disabled={!recipientCount}
              onClick={() => setStep(2)}
            >
              Continue
            </button>
          </div>
        )}

        {/* STEP 2 — payment method */}
        {step === 2 && (
          <div className="border p-4 rounded shadow">
            <h2 className="font-semibold text-lg mb-1">How are you paying?</h2>
            <p className="text-sm text-gray-500 mb-3">
              An order uses one funding source — SNPL and wallet are not combined.
            </p>

            {contextLoading ? (
              <p className="text-gray-500">Loading your available funds…</p>
            ) : (
              <>
                <label
                  className={
                    "block border rounded p-3 mb-2 cursor-pointer " +
                    (paymentMethod === "SNPL" ? "border-blue-600 bg-blue-50" : "border-gray-300") +
                    (snplUsable ? "" : " opacity-50")
                  }
                >
                  <input
                    type="radio"
                    name="pay"
                    className="mr-2"
                    disabled={!snplUsable}
                    checked={paymentMethod === "SNPL"}
                    onChange={() => setPaymentMethod("SNPL")}
                  />
                  <span className="font-semibold">Send Now Pay Later (SNPL)</span>
                  <div className="text-sm text-gray-600 ml-6">
                    {snplUsable ? `CAD ${Number(snplAvailable).toFixed(2)} available` : snplReason()}
                  </div>
                </label>

                <label
                  className={
                    "block border rounded p-3 mb-4 cursor-pointer " +
                    (paymentMethod === "WALLET" ? "border-blue-600 bg-blue-50" : "border-gray-300") +
                    (Number(walletCad || 0) > 0 ? "" : " opacity-50")
                  }
                >
                  <input
                    type="radio"
                    name="pay"
                    className="mr-2"
                    disabled={!(Number(walletCad || 0) > 0)}
                    checked={paymentMethod === "WALLET"}
                    onChange={() => setPaymentMethod("WALLET")}
                  />
                  <span className="font-semibold">Wallet balance (SNPN)</span>
                  <div className="text-sm text-gray-600 ml-6">
                    {Number(walletCad || 0) > 0
                      ? `CAD ${Number(walletCad).toFixed(2)} available`
                      : "No wallet balance — load funds first"}
                  </div>
                </label>
              </>
            )}

            <div className="flex gap-2">
              <button className="border p-2 flex-1 rounded" onClick={() => setStep(1)}>
                Back
              </button>
              <button
                className="bg-blue-600 text-white p-2 flex-1 rounded disabled:opacity-50"
                disabled={!paymentMethod}
                onClick={() => setStep(3)}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — recipients + amounts */}
        {step === 3 && (
          <div>
            <div
              className={
                "border p-3 rounded mb-4 text-sm " +
                (overCap
                  ? "bg-red-50 border-red-300 text-red-800"
                  : "bg-gray-50 border-gray-200 text-gray-700")
              }
            >
              <div className="flex justify-between font-semibold">
                <span>{paymentMethod === "SNPL" ? "SNPL credit" : "Wallet balance"}</span>
                <span>
                  CAD {runningTotal.toFixed(2)} of {availableCap.toFixed(2)} used
                </span>
              </div>
              <div className="flex justify-between">
                <span>Estimated — final price at review</span>
                <span>
                  {overCap
                    ? `Over by CAD ${Math.abs(remaining).toFixed(2)}`
                    : `CAD ${remaining.toFixed(2)} remaining`}
                </span>
              </div>
            </div>

            {slots.map((slot, i) => (
              <div key={i} className="border p-4 rounded shadow mb-3">
                <p className="font-semibold mb-2">Recipient {i + 1}</p>

                <RecipientSelector
                  onSelect={(r) => selectRecipient(i, r)}
                  selectedRecipient={slot}
                  excludeIds={chosenIds.filter((id) => !(slot && slot.id === id))}
                />

                {slot && (
                  <>
                    <p className="text-sm text-green-700 mt-2 mb-3">
                      ✓ {slot.recipientName} — {slot.recipientPhone}
                    </p>

                    <label className="block text-sm font-semibold mb-1">Top-up amount (KES)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      className="border p-2 w-full rounded mb-2"
                      placeholder="e.g. 500"
                      value={amounts[i] || ""}
                      onChange={(e) => setAmount(i, e.target.value)}
                    />
                    <div className="flex gap-2 mb-1">
                      {QUICK_AMOUNTS.map((a) => (
                        <button
                          key={a}
                          type="button"
                          className={
                            "px-3 py-1 rounded border text-sm " +
                            (Number(amounts[i]) === a
                              ? "bg-green-600 text-white border-green-600"
                              : "bg-white text-gray-700")
                          }
                          onClick={() => setAmount(i, String(a))}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                    {fieldErrors[i] ? (
                      <p className="text-xs text-red-600">{fieldErrors[i]}</p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        KES {LIMITS.AIRTIME_KES_MIN} – {LIMITS.AIRTIME_KES_MAX.toLocaleString()}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}

            <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded mb-4 text-xs">
              <strong>How it works.</strong> Mobile credit goes straight to their number.
              They can use it for calls and SMS, or buy any data bundle their network
              offers.
            </div>

            <div className="flex gap-2">
              <button className="border p-2 flex-1 rounded" onClick={() => setStep(2)}>
                Back
              </button>
              <button
                className="bg-blue-600 text-white p-2 flex-1 rounded disabled:opacity-50"
                onClick={handleGetQuote}
                disabled={loading || overCap || !online}
              >
                {loading ? "Pricing…" : "Get quote"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — review */}
        {step === 4 && quote && (
          <div>
            <h2 className="font-semibold text-lg mb-3">Review your top-up</h2>

            <div className="border p-4 rounded shadow mb-3">
              {quote.items.map((item, idx) => {
                const r = chosen[item.recipientIndex - 1];
                return (
                  <div key={idx} className="flex justify-between py-2 border-b text-sm">
                    <div>
                      <div className="font-semibold">
                        {r ? r.recipientName : `Recipient ${item.recipientIndex}`}
                      </div>
                      <div className="text-gray-500">
                        {item.destinationCurrency} {item.destinationAmount} airtime
                      </div>
                    </div>
                    <div className="text-right">CAD {item.customerPayAmount}</div>
                  </div>
                );
              })}

              <div className="text-sm mt-3">
                <Row label="Multi-recipient fee" value={quote.breakdown.multiRecipientFee} />
                {Number(quote.breakdown.snplFee) > 0 && (
                  <Row label="SNPL fee" value={quote.breakdown.snplFee} />
                )}
                <Row label="Total fees" value={quote.breakdown.totalFees} />
                <div className="flex justify-between py-2 font-bold text-base border-t mt-1">
                  <span>Total</span>
                  <span>CAD {quote.breakdown.totalAmount}</span>
                </div>
              </div>
            </div>

            {quote.bundleAndSave && (
              <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded mb-3 text-sm">
                <strong>Bundle &amp; Save.</strong> Topping up these{" "}
                {quote.bundleAndSave.recipientCount} recipients separately would cost CAD{" "}
                {quote.bundleAndSave.ifSentSeparately} in SNPL fees. You're paying CAD{" "}
                {quote.bundleAndSave.bundledFee} —{" "}
                <strong>you save CAD {quote.bundleAndSave.youSave}</strong>.
              </div>
            )}

            <p className="text-xs text-gray-500 mb-3">
              This quote expires at {new Date(quote.expiresAt).toLocaleTimeString()}.
            </p>

            <div className="flex gap-2">
              <button
                className="border p-2 flex-1 rounded"
                onClick={() => {
                  setQuote(null);
                  setStep(3);
                }}
              >
                Back
              </button>
              <button
                className="bg-green-600 text-white p-2 flex-1 rounded disabled:opacity-50"
                onClick={handleConfirm}
                disabled={loading || !online || Boolean(verification && !(verification.idVerified && verification.phoneVerified))}
              >
                {loading ? "Sending…" : order ? "Try again" : "Confirm top-up"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 5 — done */}
        {step === 5 && order && (
          <div>
            <h2 className="font-semibold text-lg mb-3">Top-up sent</h2>

            <div className="border p-4 rounded shadow mb-3 text-sm">
              <div className="flex justify-between py-1 border-b">
                <span className="text-gray-600">Reference</span>
                <span className="font-mono">{order.order.reference}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-gray-600">Status</span>
                <span className="font-semibold">{order.order.status}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-600">Total</span>
                <span>
                  {order.order.sourceCurrency} {order.order.totalAmount}
                </span>
              </div>
            </div>

            {(order.recipients || []).map((rg, i) => (
              <div key={i} className="border p-3 rounded mb-2 text-sm">
                <p className="font-semibold mb-1">
                  {chosen[rg.displayOrder - 1]
                    ? chosen[rg.displayOrder - 1].recipientName
                    : `Recipient ${rg.displayOrder}`}
                </p>
                {(rg.items || []).map((it) => (
                  <div key={it.itemReference} className="flex justify-between text-gray-600">
                    <span>
                      {it.destinationCurrency} {it.destinationAmount} airtime
                    </span>
                    <span>{it.status}</span>
                  </div>
                ))}
              </div>
            ))}

            <OrderOutcome order={order} />

            <button
              className="bg-blue-600 text-white p-2 w-full rounded"
              onClick={() => navigate("/dashboard")}
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-gray-600">{label}</span>
      <span>CAD {value}</span>
    </div>
  );
}
